import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import {
  XMarkIcon,
  UserPlusIcon,
  PlusCircleIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
  ArrowLeftIcon,
  ArrowUpTrayIcon,
  DocumentArrowDownIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { INVITE_MODAL_SIZING } from '../../constants/inviteFlowModalTheme';

const EMPTY_ROW = (patch = {}) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  firstName: '',
  lastName: '',
  email: '',
  mobile: '+91',
  ...patch,
});

// Indian mobiles are 10 digits starting 6–9 (TRAI); anything else fails at Plivo or rings a wrong person.
const VALID_MOBILE = /^\+91[6-9]\d{9}$/;
const isBlankMobile = (mobile) => !mobile || mobile === '+91';

const UPLOAD_ACCEPT = '.csv,.xlsx,.xls,.zip,.rar';

// Same shell as the standard invite modal (.invite-flow-modal-shell, styled by ScreeningDetailPage),
// pinned to its max height so the frame doesn't jump between choose / upload / review.
const { desktop, tablet, mobile } = INVITE_MODAL_SIZING;
const qaInviteShellHeight = `
  .qa-invite-shell { height: calc(${desktop.maxHeightVh}vh + ${desktop.maxHeightExtraPx}px); }
  @media (max-width: 1279px) { .qa-invite-shell { height: calc(${tablet.maxHeightVh}vh + ${tablet.maxHeightExtraPx}px); } }
  @media (max-width: 767px) { .qa-invite-shell { height: ${mobile.maxHeightVh}vh; } }
`;

function downloadTemplate() {
  const sheet = XLSX.utils.aoa_to_sheet([
    ['First name', 'Last name', 'Email (Optional)', 'Mobile'],
    ['Gladys', 'Gorilla', '', '9876543210'],
    ['Taylor', 'Patel', 'taylor-patel@emailprovider.com', '9876543211'],
  ]);
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, 'Candidates');
  XLSX.writeFile(book, 'qualifying_call_template.xlsx');
}

/**
 * Standalone invite modal for Qualifying Agent screenings — mirrors WhatsAppInviteModal.js.
 * Two ways in, one way out: type candidates in, or upload a sheet / resume archive whose
 * numbers are read by POST /upload-candidates/preview (creates nobody). Both land on the same
 * rows for review, and only "Start Qualifying Calls" places calls (POST /qualifying-agent-invite).
 * If a call isn't picked up or drops mid-way, the server falls back to WhatsApp automatically.
 * No share-link option: the agent needs a number to call.
 *
 * `mode` is the screening's channel (qualifyingAgentMode). 16 Oct: a WhatsApp-only agent showed
 * "Start Qualifying Calls" and "Started 1 qualifying call" although nobody is phoned on that
 * setting — every line below now comes from WORDING.
 */
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const WORDING = {
  whatsapp: {
    Icon: ChatBubbleLeftRightIcon,
    title: 'Invite via WhatsApp',
    choose: 'Choose how to add the candidates the agent will message',
    upload: 'Upload a sheet or resumes — you review the numbers before anything is sent',
    review: "We'll message each candidate on WhatsApp",
    nobodyYet: 'Nobody is messaged yet.',
    check: 'Check the numbers before sending.',
    start: (n) => (n > 1 ? `Send ${n} WhatsApp Invites` : 'Send WhatsApp Invite'),
    busy: 'Sending…',
    started: (n) => `WhatsApp invite sent to ${plural(n, 'candidate')}`,
    skipped: 'no new message sent',
    failed: 'Failed to send WhatsApp invites',
  },
  call: {
    Icon: PhoneIcon,
    title: 'Invite via Qualifying Call',
    choose: 'Choose how to add the candidates the agent will call',
    upload: 'Upload a sheet or resumes — you review the numbers before any call is placed',
    review: "We'll call each candidate first, then follow up on WhatsApp if needed",
    nobodyYet: 'Nobody is called yet.',
    check: 'Check the numbers before starting the calls.',
    start: (n) => (n > 1 ? `Start ${n} Qualifying Calls` : 'Start Qualifying Calls'),
    busy: 'Starting…',
    started: (n) => `Started ${plural(n, 'qualifying call')}`,
    skipped: 'no new call placed',
    failed: 'Failed to start qualifying calls',
  },
};

export default function QualifyingAgentInviteModal({ screeningId, mode = 'both', onClose, onInvited }) {
  const words =
    mode === 'whatsapp'
      ? WORDING.whatsapp
      : mode === 'call'
        ? { ...WORDING.call, review: "We'll call each candidate" }
        : WORDING.call;
  const { Icon } = words;
  const [step, setStep] = useState('choose');
  const [rows, setRows] = useState([EMPTY_ROW()]);
  const [submitting, setSubmitting] = useState(false);
  const [file, setFile] = useState(null);
  const [reading, setReading] = useState(false);
  const [fromFile, setFromFile] = useState(null);

  const updateRow = (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const handleMobileChange = (id, value) => {
    const digits = value.replace(/[^\d]/g, '');
    updateRow(id, { mobile: digits ? `+91${digits.replace(/^91/, '')}` : '+91' });
  };

  const addRow = () => setRows((prev) => [...prev, EMPTY_ROW()]);
  const removeRow = (id) => setRows((prev) => (prev.length === 1 ? prev : prev.filter((r) => r.id !== id)));

  const filled = rows.filter((r) => !isBlankMobile(r.mobile));
  const invalidCount = filled.filter((r) => !VALID_MOBILE.test(r.mobile)).length;
  const duplicateCount = filled.length - new Set(filled.map((r) => r.mobile)).size;
  const canSubmit = filled.length > 0 && invalidCount === 0 && duplicateCount === 0;
  const isDuplicate = (row) => filled.some((r) => r.id !== row.id && r.mobile === row.mobile);

  const handleReadFile = async () => {
    if (!file) return;
    setReading(true);
    try {
      const formData = new FormData();
      formData.append('candidateList', file);
      const res = await axios.post(`/api/screenings/${screeningId}/upload-candidates/preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { candidates = [], skipped = [] } = res.data || {};
      if (!candidates.length) {
        toast.error(
          skipped.length
            ? `No usable mobile numbers in ${file.name} (${skipped.length} row${skipped.length === 1 ? '' : 's'} skipped)`
            : `No candidates found in ${file.name}`
        );
        return;
      }
      setRows(
        candidates.map((c) => {
          const [firstName = '', ...rest] = (c.name || '').trim().split(/\s+/);
          return EMPTY_ROW({ firstName, lastName: rest.join(' '), email: c.email || '', mobile: c.mobile });
        })
      );
      setFromFile({ name: file.name, found: candidates.length, skipped });
      setStep('review');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not read the file');
    } finally {
      setReading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    const candidates = filled.map((r) => ({
      name: [r.firstName.trim(), r.lastName.trim()].filter(Boolean).join(' ').trim(),
      email: r.email.trim() || undefined,
      mobile: r.mobile.trim(),
    }));

    setSubmitting(true);
    try {
      const res = await axios.post(`/api/screenings/${screeningId}/qualifying-agent-invite`, { candidates });
      const { summary, errors } = res.data || {};
      if (summary?.started > 0) {
        toast.success(words.started(summary.started));
      }
      if (summary?.scheduled > 0) {
        // Outside the screening's call hours (or Plivo was briefly at capacity): queued, not lost.
        const when = summary.firstScheduledFor
          ? new Date(summary.firstScheduledFor).toLocaleString('en-IN', {
              timeZone: 'Asia/Kolkata',
              hour: 'numeric',
              minute: '2-digit',
              day: 'numeric',
              month: 'short',
            })
          : 'the next call window';
        toast.success(
          `${summary.scheduled} call${summary.scheduled === 1 ? '' : 's'} scheduled — the agent calls from ${when} IST, within the screening's call hours`,
          { duration: 7000 }
        );
      }
      if (summary?.skipped > 0) {
        toast(
          `${plural(summary.skipped, 'candidate')} already had an active session — ${words.skipped}`,
          { icon: 'ℹ️', duration: 6000 }
        );
      }
      if (summary?.failed > 0) {
        // Surface the server's actual reason ("Plivo is not configured", an unreachable webhook
        // base URL, …). A bare "failed to start" sends people hunting the phone network for what
        // is almost always a configuration fault visible right here.
        toast.error(
          errors?.[0] || `${summary.failed} invite${summary.failed === 1 ? '' : 's'} failed to start`,
          { duration: 8000 }
        );
      }
      onInvited?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || words.failed);
    } finally {
      setSubmitting(false);
    }
  };

  const subtitle = words[step];

  const inputClass = (bad) =>
    `w-full rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${
      bad
        ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-red-200'
        : 'border-slate-200 focus:border-[#007BFF] focus:ring-[#007BFF]/20'
    }`;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 sm:p-6 invite-flow-modal-overlay">
      <style dangerouslySetInnerHTML={{ __html: qaInviteShellHeight }} />
      <div className="invite-flow-modal-shell qa-invite-shell flex flex-col bg-white">
        <div style={{ height: 3, background: 'linear-gradient(90deg, #007BFF, #0062CC)' }} />
        <header className="flex items-center justify-between shrink-0 border-b border-gray-100 px-6 py-5">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-[#007BFF]">
              <Icon className="h-4.5 w-4.5 text-white" style={{ width: 18, height: 18 }} />
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight text-slate-900">{words.title}</h2>
              <p className="text-xs mt-0.5 text-slate-500">{subtitle}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </header>

        {step === 'choose' && (
          <>
          <div className="flex-1 min-h-0 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-5 px-6 py-6">
            <button
              type="button"
              data-testid="qa-invite-opt-manual"
              onClick={() => {
                setRows([EMPTY_ROW()]);
                setFromFile(null);
                setStep('review');
              }}
              className="flex flex-col items-center justify-center gap-4 rounded-2xl border-2 border-slate-200 bg-white px-6 py-8 text-center hover:border-[#4F46E5] hover:shadow-[0_0_0_3px_rgba(79,70,229,0.12)]"
            >
              <div className="rounded-2xl bg-indigo-50 p-3.5">
                <UserPlusIcon className="h-6 w-6 text-[#4F46E5]" />
              </div>
              <div>
                <p className="text-lg font-bold text-slate-900">Add candidates</p>
                <p className="mt-1.5 text-sm text-slate-500">Type in names and mobile numbers yourself.</p>
              </div>
            </button>
            <button
              type="button"
              data-testid="qa-invite-opt-bulk"
              onClick={() => {
                setFile(null);
                setStep('upload');
              }}
              className="flex flex-col items-center justify-center gap-4 rounded-2xl border-2 border-slate-200 bg-white px-6 py-8 text-center hover:border-[#10B981] hover:shadow-[0_0_0_3px_rgba(16,185,129,0.12)]"
            >
              <div className="rounded-2xl bg-emerald-50 p-3.5">
                <ArrowUpTrayIcon className="h-6 w-6 text-[#10B981]" />
              </div>
              <div>
                <p className="text-lg font-bold text-slate-900">Invite in bulk</p>
                <p className="mt-1.5 text-sm text-slate-500">
                  Upload an Excel/CSV sheet or a ZIP of resumes — we read the mobile numbers for you to check.
                </p>
              </div>
            </button>
          </div>
          <footer className="flex-shrink-0 px-6 py-4 border-t border-gray-100 flex justify-between">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100"
            >
              <XMarkIcon className="h-4 w-4" />
              Close
            </button>
          </footer>
          </>
        )}

        {step === 'upload' && (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6 space-y-5">
              <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-sm text-slate-600">
                  Sheet columns: <span className="font-medium">First name, Last name, Email (Optional), Mobile</span>
                </p>
                <button
                  type="button"
                  onClick={downloadTemplate}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-[#0062CC] hover:bg-[#007BFF]/5"
                >
                  <DocumentArrowDownIcon className="h-4 w-4" />
                  Template
                </button>
              </div>
              <label className="flex cursor-pointer flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 px-6 py-10 text-center hover:border-[#007BFF]">
                <ArrowUpTrayIcon className="h-7 w-7 text-slate-400" />
                <span className="text-sm font-semibold text-slate-700">
                  {file ? file.name : 'Choose a file'}
                </span>
                <span className="text-xs text-slate-500">
                  .csv, .xlsx, .xls, or .zip/.rar of resumes (PDF, DOCX, DOC) — up to 50 MB
                </span>
                <input
                  type="file"
                  data-testid="qa-invite-file"
                  accept={UPLOAD_ACCEPT}
                  className="sr-only"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </label>
              <p className="text-xs text-slate-500">
                {words.nobodyYet} Rows without a valid mobile, and repeated numbers, are left out and listed on the next screen.
              </p>
            </div>
            <footer className="flex-shrink-0 px-6 py-4 border-t border-gray-100 flex justify-between">
              <button
                type="button"
                onClick={() => setStep('choose')}
                className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Back
              </button>
              <button
                type="button"
                data-testid="qa-invite-read-file"
                onClick={handleReadFile}
                disabled={!file || reading}
                className="inline-flex items-center gap-2 rounded-xl bg-[#007BFF] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0062CC] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {reading ? 'Reading…' : 'Read numbers'}
              </button>
            </footer>
          </>
        )}

        {step === 'review' && (
          <>
            <form id="qualifying-agent-invite-form" onSubmit={handleSubmit} className="flex-1 min-h-0 flex flex-col overflow-hidden">
              {fromFile && (
                <div className="shrink-0 border-b border-slate-100 px-6 py-3 text-sm" data-testid="qa-invite-file-summary">
                  <p className="text-slate-700">
                    Found <span className="font-semibold">{fromFile.found}</span> candidate{fromFile.found === 1 ? '' : 's'} in{' '}
                    <span className="font-medium">{fromFile.name}</span>. {words.check}
                  </p>
                  {fromFile.skipped.length > 0 && (
                    <details className="mt-1.5">
                      <summary className="cursor-pointer text-amber-700">
                        <ExclamationTriangleIcon className="mr-1 inline h-4 w-4 align-text-bottom" />
                        {fromFile.skipped.length} left out
                      </summary>
                      <ul className="mt-1.5 max-h-28 overflow-y-auto pl-5 text-xs text-slate-600 list-disc">
                        {fromFile.skipped.map((s, i) => (
                          <li key={i}>
                            {s.label}: {s.reason}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
              <div className="shrink-0 border-b border-slate-100 px-6 py-3 bg-[#007BFF]/5">
                <div className="grid grid-cols-[1fr,1fr,1.2fr,1.2fr,0.6fr] items-center text-xs font-bold text-slate-700">
                  <span>First name</span>
                  <span>Last name</span>
                  <span>Email</span>
                  <span>Mobile</span>
                  <span className="sr-only">Actions</span>
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-3">
                {rows.map((row, index) => {
                  const badMobile =
                    !isBlankMobile(row.mobile) && (!VALID_MOBILE.test(row.mobile) || isDuplicate(row));
                  return (
                    <div key={row.id} className="grid grid-cols-[1fr,1fr,1.2fr,1.2fr,0.6fr] items-center gap-3">
                      <input
                        type="text"
                        value={row.firstName}
                        onChange={(e) => updateRow(row.id, { firstName: e.target.value })}
                        placeholder="First name"
                        className={inputClass(false)}
                      />
                      <input
                        type="text"
                        value={row.lastName}
                        onChange={(e) => updateRow(row.id, { lastName: e.target.value })}
                        placeholder="Last name"
                        className={inputClass(false)}
                      />
                      <input
                        type="email"
                        value={row.email}
                        onChange={(e) => updateRow(row.id, { email: e.target.value })}
                        placeholder="Email (Optional)"
                        className={inputClass(false)}
                      />
                      <input
                        type="text"
                        required={index === 0}
                        value={row.mobile}
                        onChange={(e) => handleMobileChange(row.id, e.target.value)}
                        placeholder="Mobile *"
                        aria-invalid={badMobile}
                        title={badMobile ? (isDuplicate(row) ? 'This number is already in the list' : 'Enter a 10-digit Indian mobile') : undefined}
                        className={inputClass(badMobile)}
                      />
                      {index === 0 ? (
                        <button
                          type="button"
                          onClick={addRow}
                          className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-semibold text-[#0062CC] hover:border-[#007BFF]/40 hover:bg-[#007BFF]/5"
                        >
                          <PlusCircleIcon className="h-4 w-4" />
                          Add
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => removeRow(row.id)}
                          className="inline-flex w-full items-center justify-center rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-medium text-slate-500 hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </form>

            <footer className="flex-shrink-0 px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={() => setStep(fromFile ? 'upload' : 'choose')}
                className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Back
              </button>
              <div className="flex items-center gap-3">
                {(invalidCount > 0 || duplicateCount > 0) && (
                  <span className="text-xs text-red-600" data-testid="qa-invite-fix-numbers">
                    Fix or remove the highlighted numbers
                  </span>
                )}
                <button
                  type="submit"
                  form="qualifying-agent-invite-form"
                  disabled={!canSubmit || submitting}
                  className="inline-flex items-center gap-2 rounded-xl bg-[#007BFF] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0062CC] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <UserPlusIcon className="h-4 w-4" />
                  <span>
                    {submitting ? words.busy : words.start(filled.length)}
                  </span>
                </button>
              </div>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}
